import React, { useState } from "react";
import { toast } from "sonner";
import { FileText, Youtube, Trash2, Loader2, Plus, Library } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function MediaLibrarySection({ courseId, resources, onChanged }) {
  const [type, setType] = useState("pdf");
  const [title, setTitle] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const addResource = async (fileForPdf) => {
    if (!title.trim()) { toast.error("Give this resource a title"); return; }
    setBusy(true);
    try {
      let url = youtubeUrl.trim();
      if (type === "pdf") {
        if (!fileForPdf) { toast.error("Please choose a PDF file"); setBusy(false); return; }
        const formData = new FormData();
        formData.append("file", fileForPdf);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        url = data.url;
      } else if (!url) {
        toast.error("Paste a YouTube link"); setBusy(false); return;
      }
      await api.post(`/courses/${courseId}/resources`, { type, title, url });
      toast.success("Resource added to Media Library!");
      setTitle(""); setYoutubeUrl("");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add resource");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (resourceId) => {
    try {
      await api.delete(`/courses/${courseId}/resources/${resourceId}`);
      toast.success("Resource removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mb-6" data-testid="media-library-section">
      <h3 className="font-heading text-sm font-semibold text-white mb-3 flex items-center gap-1.5"><Library className="w-4 h-4 text-teal-400" /> Media Library</h3>
      <div className="space-y-2 mb-4">
        {resources.length === 0 && <p className="text-xs text-slate-500">No PDFs or YouTube links added yet.</p>}
        {resources.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3.5 py-2.5" data-testid={`media-resource-row-${r.id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              {r.type === "pdf" ? <FileText className="w-4 h-4 text-rose-300 shrink-0" /> : <Youtube className="w-4 h-4 text-red-400 shrink-0" />}
              <a href={r.url} target="_blank" rel="noreferrer" className="text-sm text-slate-200 hover:text-white truncate">{r.title}</a>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`media-resource-delete-${r.id}`} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <div className="glass-panel rounded-xl p-4">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setType("pdf")} data-testid="media-library-type-pdf" className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${type === "pdf" ? "bg-primary text-white" : "bg-white/5 text-slate-400"}`}>PDF</button>
          <button onClick={() => setType("youtube")} data-testid="media-library-type-youtube" className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${type === "youtube" ? "bg-primary text-white" : "bg-white/5 text-slate-400"}`}>YouTube Link</button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Resource title" data-testid="media-library-title-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-primary/50 mb-2.5" />
        {type === "youtube" ? (
          <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="media-library-youtube-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-primary/50 mb-2.5" />
        ) : null}
        {type === "pdf" ? (
          <label className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-dashed border-white/15 rounded-lg px-4 py-2.5 text-sm text-slate-300 cursor-pointer transition-colors mb-2.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Choose PDF file
            <input type="file" accept="application/pdf" onChange={(e) => e.target.files?.[0] && addResource(e.target.files[0])} disabled={busy} data-testid="media-library-pdf-input" className="hidden" />
          </label>
        ) : (
          <button onClick={() => addResource(null)} disabled={busy} data-testid="media-library-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Resource
          </button>
        )}
      </div>
    </div>
  );
}
